import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../lib/api-client';
import { emailKey } from '../../lib/email-api';
import { keys } from '../../lib/queries';
import type { Personalization } from '../../lib/api-types';
import styles from './LearningNotice.module.css';

type Review = { id: string; status: string; summary: string | null; error?: string | null; conversationId: string;
  changes?: Array<{ field: 'memory' | 'goals' | 'mission'; operation: string; before: string; after: string; evidence: string }> };
const path = (id: string) => `/v1/settings/learning/${encodeURIComponent(id)}`;
const scope = () => ['learning', ...emailKey()];
const pending = (status?: string) => status === 'pending' || status === 'running';
const labels = { memory: 'Memory', goals: 'Goals', mission: 'Current mission' };

export function LearningNotice({ id }: { id: string }) {
  const cache = useQueryClient();
  const review = useQuery({ queryKey: [...scope(), id], queryFn: () => request<Review>(path(id)), retry: false,
    refetchInterval: query => pending(query.state.data?.status) ? 2000 : false });
  const action = useMutation({ mutationFn: (operation: 'undo' | 'retry') => request(`${path(id)}/${operation}`, { method: 'POST', personalKey: operation === 'retry' }),
    onSuccess: async () => { await Promise.all([cache.invalidateQueries({ queryKey: scope() }), cache.invalidateQueries({ queryKey: keys.personalization })]); } });
  useEffect(() => {
    if (review.data && !pending(review.data.status)) void cache.invalidateQueries({ queryKey: keys.personalization });
  }, [cache, review.data?.status]);
  if (review.isError) return <p className={styles.notice} role="status">Memory review status is unavailable. <button onClick={() => void review.refetch()}>Retry</button></p>;
  const data = review.data;
  if (!data || data.status === 'no_change') return null;
  if (pending(data.status)) return <p className={styles.notice} role="status">Updating memory from this conversation…</p>;
  if (data.status === 'reverted') return <p className={styles.notice}>Memory changes undone.</p>;
  if (data.status === 'failed') return <div className={styles.notice} role="status">
    <p>Couldn’t finish remembering this conversation. <button disabled={action.isPending} onClick={() => action.mutate('retry')}>Try again</button></p>
    {action.error && <p role="alert">{action.error.message}</p>}
  </div>;
  return <details className={styles.notice}>
    <summary>{data.summary ?? 'Updated your saved context.'} <span>View changes</span></summary>
    {(data.changes ?? []).map((change, i) => <section key={i} className={styles.change}>
      <strong>{labels[change.field]}</strong>
      {change.before && <><small>Before</small><p>{change.before}</p></>}
      <small>{change.after ? 'Now' : 'Removed'}</small>{change.after && <p>{change.after}</p>}
      <small>From your message</small><blockquote>{change.evidence}</blockquote>
    </section>)}
    <button disabled={action.isPending} onClick={() => action.mutate('undo')}>Undo these changes</button>
    {action.error && <p role="alert">{action.error.message}</p>}
  </details>;
}

export function HomeLearningStatus({ personal }: { personal: Personalization }) {
  const learning = personal.learning;
  if (!learning) return null;
  return <>
    {learning.pending > 0 && <p className={styles.notice} role="status">Updating memory and goals from your conversation…</p>}
    {learning.failed > 0 && <p className={styles.notice}>Some memory updates need a retry. <a href="/app/settings/ai-memory">Review updates</a></p>}
    {learning.latest && !pending(learning.latest.status) && <LearningNotice id={learning.latest.id} />}
  </>;
}

export function LearningHistory() {
  const query = useQuery({ queryKey: [...scope(), 'history'], queryFn: () => request<{ reviews: Review[] }>('/v1/settings/learning'),
    refetchInterval: q => q.state.data?.reviews.some(r => pending(r.status)) ? 2000 : false });
  return <section className={styles.history}><h2>Learned from conversations</h2>
    {query.isError && <p>Couldn’t load memory updates. <button onClick={() => void query.refetch()}>Retry</button></p>}
    {query.data?.reviews.length === 0 && <p>Confirmed facts and goal changes from new conversations will appear here.</p>}
    {query.data?.reviews.map(review => <div key={review.id}><LearningNotice id={review.id} /><a className={styles.source} href={`/app/c/${encodeURIComponent(review.conversationId)}`}>Open conversation ↗</a></div>)}
  </section>;
}
